import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Header } from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  Loader2, 
  User, 
  MapPin, 
  Image, 
  FileText, 
  Tag, 
  Phone, 
  Instagram,
  Mail,
  CheckCircle,
  ArrowLeft,
  Upload
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BecomeTrainer() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    photo_url: '',
    location: '',
    bio: '',
    specialties: '',
    whatsapp: '',
    instagram: ''
  });

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const response = await axios.get(`${API_URL}/payments/pricing`);
        setPricing(response.data);
      } catch (error) {
        console.error('Failed to fetch pricing');
      }
    };
    fetchPricing();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(file.type)) {
      toast.error('Please upload a JPEG, PNG, WebP, or GIF image');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5MB');
      return;
    }

    // Show preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPhotoPreview(reader.result);
    };
    reader.readAsDataURL(file);

    // Upload to server
    setUploading(true);
    try {
      const uploadData = new FormData();
      uploadData.append('file', file);

      const response = await axios.post(`${API_URL}/upload/photo`, uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setFormData(prev => ({ ...prev, photo_url: response.data.photo_url }));
      toast.success('Photo uploaded successfully!');
    } catch (error) {
      toast.error('Failed to upload photo');
      setPhotoPreview(null);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.email || !formData.photo_url || !formData.location || !formData.bio || !formData.specialties) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!formData.whatsapp && !formData.instagram) {
      toast.error('Please provide at least one contact method (WhatsApp or Instagram)');
      return;
    }

    if (!termsAccepted) {
      toast.error('You must accept the Terms & Conditions');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/trainers/apply`, { ...formData, terms_accepted: termsAccepted });
      setSubmitted(true);
      toast.success('Application submitted successfully!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to submit application';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0B0B0B]" data-testid="become-trainer-success">
        <Header />
        <main className="pt-24 px-4 sm:px-6 lg:px-8 pb-16">
          <div className="max-w-md mx-auto text-center">
            <div className="bg-[#141414] rounded-2xl p-8 border border-white/5 animate-fade-in-up">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-green-500/20 flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <h1 className="font-heading text-3xl font-bold uppercase text-white mb-4">
                Application Submitted!
              </h1>
              <p className="text-gray-400 mb-6">
                Thank you for applying to join TRAINR. We'll review your application and get back to you soon.
              </p>
              <Link to="/">
                <Button className="bg-primary text-white hover:bg-primary/90">
                  Back to Home
                </Button>
              </Link>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0B0B]" data-testid="become-trainer-page">
      <Header />
      
      <main className="pt-24 px-4 sm:px-6 lg:px-8 pb-16">
        <div className="max-w-2xl mx-auto">
          {/* Back Link */}
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>

          {/* Header */}
          <div className="text-center mb-8 animate-fade-in-up">
            <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tighter leading-none uppercase text-white mb-3">
              Become a <span className="text-primary">Trainer</span>
            </h1>
            <p className="text-gray-400 text-lg">
              Join our platform and connect with clients looking for expert trainers like you.
            </p>
          </div>

          {/* Form */}
          <div className="bg-[#141414] rounded-2xl p-6 sm:p-8 border border-white/5 animate-fade-in-up animation-delay-100">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="name" className="text-gray-400 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Full Name *
                </Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="John Smith"
                  value={formData.name}
                  onChange={handleChange}
                  className="h-12 bg-[#1A1A1A] border-transparent text-white placeholder:text-gray-500 focus:ring-2 focus:ring-primary focus:border-transparent rounded-xl"
                  data-testid="trainer-name-input"
                />
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-gray-400 flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Contact Email *
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  className="h-12 bg-[#1A1A1A] border-transparent text-white placeholder:text-gray-500 focus:ring-2 focus:ring-primary focus:border-transparent rounded-xl"
                  data-testid="trainer-email-input"
                />
              </div>

              {/* Photo Upload */}
              <div className="space-y-2">
                <Label className="text-gray-400 flex items-center gap-2">
                  <Image className="w-4 h-4" />
                  Profile Photo *
                </Label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={handlePhotoUpload}
                  className="hidden"
                  data-testid="trainer-photo-file-input"
                />
                <div 
                  onClick={() => fileInputRef.current?.click()}
                  className={`relative h-40 rounded-xl border-2 border-dashed cursor-pointer transition-all ${
                    photoPreview ? 'border-primary' : 'border-white/20 hover:border-white/40'
                  } bg-[#1A1A1A] flex items-center justify-center overflow-hidden`}
                >
                  {uploading ? (
                    <div className="flex flex-col items-center gap-2">
                      <Loader2 className="w-8 h-8 text-primary animate-spin" />
                      <span className="text-gray-400 text-sm">Uploading...</span>
                    </div>
                  ) : photoPreview ? (
                    <img 
                      src={photoPreview} 
                      alt="Preview" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="flex flex-col items-center gap-2 p-4 text-center">
                      <Upload className="w-8 h-8 text-gray-500" />
                      <span className="text-gray-400 text-sm">Click to upload photo</span>
                      <span className="text-gray-500 text-xs">JPEG, PNG, WebP or GIF (max 5MB)</span>
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-500">Use a professional headshot or training photo</p>
              </div>

              {/* Location */}
              <div className="space-y-2">
                <Label htmlFor="location" className="text-gray-400 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Location *
                </Label>
                <Input
                  id="location"
                  name="location"
                  type="text"
                  placeholder="Los Angeles, CA"
                  value={formData.location}
                  onChange={handleChange}
                  className="h-12 bg-[#1A1A1A] border-transparent text-white placeholder:text-gray-500 focus:ring-2 focus:ring-primary focus:border-transparent rounded-xl"
                  data-testid="trainer-location-input"
                />
              </div>

              {/* Bio */}
              <div className="space-y-2">
                <Label htmlFor="bio" className="text-gray-400 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Bio *
                </Label>
                <Textarea
                  id="bio"
                  name="bio"
                  placeholder="Tell potential clients about your experience, certifications, and training style..."
                  value={formData.bio}
                  onChange={handleChange}
                  className="min-h-[120px] bg-[#1A1A1A] border-transparent text-white placeholder:text-gray-500 focus:ring-2 focus:ring-primary focus:border-transparent rounded-xl resize-none"
                  data-testid="trainer-bio-input"
                />
              </div>

              {/* Specialties */}
              <div className="space-y-2">
                <Label htmlFor="specialties" className="text-gray-400 flex items-center gap-2">
                  <Tag className="w-4 h-4" />
                  Specialties *
                </Label>
                <Input
                  id="specialties"
                  name="specialties"
                  type="text"
                  placeholder="Strength Training, HIIT, Weight Loss"
                  value={formData.specialties}
                  onChange={handleChange}
                  className="h-12 bg-[#1A1A1A] border-transparent text-white placeholder:text-gray-500 focus:ring-2 focus:ring-primary focus:border-transparent rounded-xl"
                  data-testid="trainer-specialties-input"
                />
                <p className="text-xs text-gray-500">Separate multiple specialties with commas</p>
              </div>

              {/* Contact Methods */}
              <div className="border-t border-white/5 pt-6">
                <p className="text-gray-400 text-sm mb-4">Contact Methods (at least one required)</p>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* WhatsApp */}
                  <div className="space-y-2">
                    <Label htmlFor="whatsapp" className="text-gray-400 flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      WhatsApp Number
                    </Label>
                    <Input
                      id="whatsapp"
                      name="whatsapp"
                      type="tel"
                      placeholder="+1234567890"
                      value={formData.whatsapp}
                      onChange={handleChange}
                      className="h-12 bg-[#1A1A1A] border-transparent text-white placeholder:text-gray-500 focus:ring-2 focus:ring-primary focus:border-transparent rounded-xl"
                      data-testid="trainer-whatsapp-input"
                    />
                  </div>

                  {/* Instagram */}
                  <div className="space-y-2">
                    <Label htmlFor="instagram" className="text-gray-400 flex items-center gap-2">
                      <Instagram className="w-4 h-4" />
                      Instagram Username
                    </Label>
                    <Input
                      id="instagram"
                      name="instagram"
                      type="text"
                      placeholder="your_username"
                      value={formData.instagram}
                      onChange={handleChange}
                      className="h-12 bg-[#1A1A1A] border-transparent text-white placeholder:text-gray-500 focus:ring-2 focus:ring-primary focus:border-transparent rounded-xl"
                      data-testid="trainer-instagram-input"
                    />
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={loading}
                className="w-full h-14 bg-primary text-white font-bold uppercase tracking-wider rounded-xl hover:shadow-[0_0_30px_rgba(59,130,246,0.6)] transition-all transform hover:-translate-y-0.5 active:scale-95 disabled:opacity-50"
                data-testid="submit-application-button"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  'Submit Application'
                )}
              </Button>

              <div className="bg-[#1A1A1A] rounded-xl p-4 mt-4">
                <h4 className="font-semibold text-white mb-2">Trainer Pricing</h4>
                <ul className="text-sm text-gray-400 space-y-1">
                  <li>• <span className="text-primary">£{pricing?.trainer_fees?.lead_unlock?.price || 4}</span> per lead unlock (deducted from session earnings)</li>
                  <li>• <span className="text-primary">£{pricing?.trainer_fees?.premium_monthly?.price || 25}/month</span> for Premium (unlimited leads + priority listing)</li>
                  <li>• Platform takes {pricing?.platform_commission || 15}% commission per session</li>
                </ul>
              </div>

              {/* Terms & Conditions Checkbox */}
              <div className="flex items-start space-x-3 p-4 bg-[#1A1A1A] rounded-xl border border-white/5">
                <Checkbox
                  id="terms"
                  checked={termsAccepted}
                  onCheckedChange={setTermsAccepted}
                  className="mt-0.5 border-gray-600 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                  data-testid="trainer-terms-checkbox"
                />
                <Label htmlFor="terms" className="text-sm text-gray-400 leading-relaxed cursor-pointer">
                  I have read and agree to the{' '}
                  <Link to="/terms" className="text-primary hover:underline" target="_blank">
                    Trainer Terms & Conditions
                  </Link>
                  , including: 100% refund to users if I miss a session, platform commission of 15% (10% for Premium), and the requirement to provide payout information to receive funds.
                </Label>
              </div>

              <p className="text-center text-gray-500 text-sm">
                By submitting, you agree to our review process. Approved trainers will be listed on the platform.
              </p>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
}
